import type { Task } from "@app/core";
import { cn, Repeat } from "@app/ui";
import { useState, type MouseEvent } from "react";
import { useTimeFormat } from "../../lib/useTimeFormat.js";
import { useNestHoverStore } from "../../stores/index.js";
import { priorityToneClass } from "../task-colors.js";
import { washStyle } from "../block-wash.js";
import { useMatrixPriorityColor } from "../priority-matrix/useEffectiveMatrixDisplay.js";
import { useCalendarInheritedTask, useCalendarProgress, useCalendarRenderIndex } from "./CalendarRenderIndex.js";
import { TaskContextMenu } from "./TaskContextMenu.js";
import { setDragTaskId } from "./drag.js";
import { FaintOverlay } from "./FaintOverlay.js";
import { useNestDrop } from "./useNestDrop.js";
import { useFlashHighlight } from "./useFlashHighlight.js";
import { usePastEventFlag } from "./usePastEventFlag.js";
import { isMobileUx } from "../../lib/useMobileUx.js";
import { suppressMobileContextMenu } from "../../lib/mobile-context-menu.js";
import { mobileBlockStyle } from "../mobile/mobile-block-style.js";

interface Props {
  task: Task;
  onClick?: (task: Task) => void;
  /** Show the hour/minute prefix (used in month view for non-all-day tasks). */
  withTime?: boolean;
  /** Make chip fill the container width (used in week/day all-day row). */
  block?: boolean;
  /** Tighter chip for phone month cells. */
  compact?: boolean;
}

export function EventChip({ task, onClick, withTime, block, compact }: Props) {
  const index = useCalendarRenderIndex();
  const inheritedTask = useCalendarInheritedTask(task);
  const done = task.status === "done";
  const pastEvent = usePastEventFlag(task);
  const tf = useTimeFormat();
  const nestDrop = useNestDrop();
  const armed = useNestHoverStore((s) => s.armedId === task.id);
  const time = withTime && task.due_at && !task.all_day ? tf.hmDate(new Date(task.due_at)) : null;
  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number } | null>(null);
  const { ref: flashRef, flash } = useFlashHighlight(task);

  const progress = useCalendarProgress(task.id);

  function handleClick(e: MouseEvent) {
    e.stopPropagation();
    onClick?.(task);
  }

  const contextColor = index.contextColorOf(task);
  const { tint, gradient } = index.blockStyle;
  // Matrix scope: continuous diagonal color for the priority accent/fill
  // (placed-only — see useMatrixPriorityColor).
  const priorityColor = useMatrixPriorityColor(inheritedTask);
  const { vars: customStyle, washed: hasCustomStyle } = washStyle(inheritedTask, contextColor, tint, gradient, priorityColor);
  const mob = isMobileUx()
    ? mobileBlockStyle({
        priority: task.priority,
        matrixColor: priorityColor,
        customColor: inheritedTask.custom_color,
        topicColors: index.topicColorsOf(task),
        calendarColor: index.calendarColorOf(task),
        isEvent: task.is_event,
      })
    : null;

  return (
    <>
      <div
        ref={flashRef}
        data-chip
        data-task-id={task.id}
        // Desktop-only native DnD — on mobile a draggable node lets the Android
        // WebView start a native drag, which Samsung offers to "open in split
        // window" at the screen edge. Touch moves go through the touch layer.
        draggable={!mob}
        onDragStart={(e) => setDragTaskId(e, task.id)}
        onDragOver={(e) => nestDrop.onDragOver(e, task)}
        onDragLeave={(e) => nestDrop.onDragLeave(e)}
        onDrop={(e) => nestDrop.onDrop(e, task)}
        onClick={handleClick}
        onContextMenu={(e) => {
          if (suppressMobileContextMenu(e)) return;
          e.preventDefault();
          e.stopPropagation();
          setCtxMenu({ x: e.clientX, y: e.clientY });
        }}
        title={task.virtual ? `${task.title} (recurring)` : task.title}
        style={mob ? mob.container : (hasCustomStyle ? customStyle : undefined)}
        className={cn(
          "relative flex cursor-pointer items-center truncate overflow-hidden rounded transition-opacity hover:ring-1 hover:ring-ring/40 active:opacity-70",
          compact && "month-event-chip",
          compact ? "h-4 gap-0.5 px-1 py-0 text-[calc(10px*var(--app-text-scale))]" : "gap-1 px-1.5 py-0.5 text-[calc(11px*var(--app-text-scale))]",
          block && "w-full",
          mob && "border bg-card",
          !mob && hasCustomStyle && "task-wash",
          !mob && !hasCustomStyle && priorityToneClass(task.priority, "chip"),
          done && "opacity-50 line-through",
          task.virtual && "opacity-80 border border-dashed border-current/30",
          armed && "scale-[1.05] ring-2 ring-primary brightness-110",
          flash && "flash-highlight",
        )}
      >
        <span
          data-block-title
          className={cn("month-event-chip-title min-w-0 truncate", time && "flex-1")}
          style={mob ? mob.titleText : undefined}
        >
          {task.title}
        </span>
        {time && (
          <span
            className={cn("month-event-chip-time min-w-0 shrink-[999] truncate font-mono", !mob && "opacity-80")}
            style={mob ? { color: mob.metaColors.time } : undefined}
          >
            {time}
          </span>
        )}
        {progress.total > 0 && (
          <span className={cn("month-event-chip-progress ml-auto shrink-0 rounded bg-black/10 px-1 tabular-nums dark:bg-white/15", compact ? "text-[calc(8px*var(--app-text-scale))]" : "text-[calc(9px*var(--app-text-scale))]")}>
            {progress.done}/{progress.total}
          </span>
        )}
        {task.recurrence_id && (
          // Series marker at the chip's right end, inked in a near-shade of the
          // chip's own fill (`--c-ink`, block-wash) so it reads on any color.
          <Repeat
            aria-hidden
            className={cn(compact ? "h-2 w-2" : "h-2.5 w-2.5", "shrink-0", progress.total === 0 && "ml-auto")}
            style={{ color: "var(--c-ink, currentColor)" }}
          />
        )}
        {!done && pastEvent && <FaintOverlay />}
      </div>

      {ctxMenu && (
        <TaskContextMenu
          task={task}
          x={ctxMenu.x}
          y={ctxMenu.y}
          onClose={() => setCtxMenu(null)}
        />
      )}
    </>
  );
}
